import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { Monitor, RotateCw, ExternalLink, X, Eye } from "lucide-react";
import {
  previewUrl,
  previewBaseUrl,
  postScrollToSection,
  isPreviewReadyMessage,
} from "@/lib/preview-bridge";

/** Tracks whether the viewport is at the `lg` breakpoint (>= 1024px). */
function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(min-width: 1024px)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = () => setIsDesktop(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return isDesktop;
}

/**
 * Embedded live preview of the public site.
 *
 * Loads `path` in an <iframe>, navigates when `path` changes, hard-reloads when
 * `reloadToken` bumps (after a save), and asks the embedded site to scroll to
 * `section` — both proactively and again whenever the site re-announces it is
 * ready (the handshake survives navigations/reloads).
 */
export function LivePreviewPane({
  path,
  section,
  reloadToken,
  placeholder,
}: {
  path: string;
  section: string | null;
  reloadToken: number;
  /** When set, shown instead of the iframe (e.g. unsaved / unpublished). */
  placeholder?: ReactNode;
}) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const sectionRef = useRef<string | null>(section);
  sectionRef.current = section;

  // Navigate / reload the iframe when the target path or reload token changes.
  useEffect(() => {
    if (placeholder) return;
    const frame = iframeRef.current;
    if (!frame) return;
    frame.src = previewUrl(path, reloadToken);
  }, [path, reloadToken, placeholder]);

  // Re-scroll when the focused section changes (best-effort; the ready
  // handshake re-posts after (re)loads in case this lands too early).
  useEffect(() => {
    if (placeholder || !section) return;
    postScrollToSection(iframeRef.current?.contentWindow, section);
  }, [section, placeholder]);

  // When the embedded site announces it is ready, (re)send the current section.
  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (isPreviewReadyMessage(event.data) && sectionRef.current) {
        postScrollToSection(iframeRef.current?.contentWindow, sectionRef.current);
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  function reload() {
    const frame = iframeRef.current;
    if (!frame) return;
    try {
      frame.contentWindow?.location.reload();
    } catch {
      frame.src = previewUrl(path, reloadToken + 1);
    }
  }

  return (
    <div className="flex flex-col h-full bg-brand-100">
      <div className="flex items-center gap-2 px-3 h-10 border-b border-brand-200 bg-white shrink-0">
        <Monitor size={14} className="text-brand-400 shrink-0" />
        <span className="text-xs text-brand-500 font-mono truncate flex-1">
          {path}
        </span>
        <button
          type="button"
          onClick={reload}
          title="Előnézet frissítése"
          className="p-1.5 rounded text-brand-500 hover:bg-brand-100 hover:text-brand-900"
        >
          <RotateCw size={14} />
        </button>
        <a
          href={`${previewBaseUrl()}${path}`}
          target="_blank"
          rel="noopener noreferrer"
          title="Megnyitás új lapon"
          className="p-1.5 rounded text-brand-500 hover:bg-brand-100 hover:text-brand-900"
        >
          <ExternalLink size={14} />
        </a>
      </div>
      <div className="relative flex-1 min-h-0">
        {placeholder ? (
          <div className="absolute inset-0 grid place-items-center p-8 text-center">
            {placeholder}
          </div>
        ) : (
          <iframe
            ref={iframeRef}
            title="Élő előnézet"
            className="absolute inset-0 w-full h-full border-0 bg-white"
          />
        )}
      </div>
    </div>
  );
}

/** Default form share of the split, in percent. */
const DEFAULT_SPLIT_RATIO = 45;
const SPLIT_STORAGE_KEY = "admin.splitRatio";
/** Minimum widths (px) so neither pane is squeezed unusable. */
const MIN_FORM_PX = 360;
const MIN_PREVIEW_PX = 320;
const RESIZER_PX = 6;

/**
 * Split-screen admin editor shell: form on the left (~45% by default), live
 * preview on the right. A draggable divider between them lets the user rebalance
 * the two panes (persisted); double-clicking the divider restores the default
 * ratio. Below the `lg` breakpoint the preview is hidden and reachable through
 * an "Előnézet" button that opens a full-screen overlay — the divider and the
 * stored ratio do not apply there.
 */
export function SplitEditor({
  title,
  subtitle,
  toolbar,
  preview,
  children,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Right-aligned controls in the top bar (e.g. save status + Save button). */
  toolbar?: ReactNode;
  preview: ReactNode;
  children: ReactNode;
}) {
  const [mobilePreviewOpen, setMobilePreviewOpen] = useState(false);
  const isDesktop = useIsDesktop();

  // Form pane width as a percentage of the split, persisted across reloads.
  const [ratio, setRatio] = useState<number>(() => {
    try {
      const stored = parseFloat(localStorage.getItem(SPLIT_STORAGE_KEY) || "");
      return Number.isFinite(stored) && stored >= 10 && stored <= 90
        ? stored
        : DEFAULT_SPLIT_RATIO;
    } catch {
      return DEFAULT_SPLIT_RATIO;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(SPLIT_STORAGE_KEY, String(ratio));
    } catch {
      /* ignore */
    }
  }, [ratio]);

  const [dragging, setDragging] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  const onResizerPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const container = bodyRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const total = rect.width;
      if (total <= 0) return;
      // Clamp the divider so both panes keep their minimum widths.
      const minX = MIN_FORM_PX;
      const maxX = total - MIN_PREVIEW_PX - RESIZER_PX;
      const x = Math.max(minX, Math.min(maxX, e.clientX - rect.left));
      setRatio((x / total) * 100);
    },
    [],
  );

  const onResizerPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      setDragging(true);
    },
    [],
  );

  const onResizerPointerUp = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      setDragging(false);
    },
    [],
  );

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      {/* Top bar */}
      <header className="shrink-0 flex items-center gap-3 px-4 lg:px-6 h-16 border-b border-brand-200 bg-white">
        <div className="min-w-0 flex-1">
          <h1 className="text-lg font-bold truncate">{title}</h1>
          {subtitle && (
            <p className="text-xs text-brand-500 truncate">{subtitle}</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => setMobilePreviewOpen(true)}
          className="btn-secondary !py-2 !px-4 text-xs lg:hidden shrink-0"
        >
          <Eye size={14} />
          Előnézet
        </button>
        <div className="flex items-center gap-3 shrink-0">{toolbar}</div>
      </header>

      {/* Split body */}
      <div
        ref={bodyRef}
        className="flex-1 min-h-0 flex flex-col lg:flex-row relative"
      >
        <div
          className="overflow-y-auto bg-brand-50 flex-1 min-h-0 lg:flex-none"
          style={isDesktop ? { width: `${ratio}%` } : undefined}
        >
          {/* No max-width cap: the draggable divider controls the pane width,
              so the form boxes should use ALL of it — a cap left dead space
              between the panes and kept wide tables on a horizontal
              scrollbar (F-MANUAL-9VTJ5561 tester report). */}
          <div className="p-5 lg:p-8">{children}</div>
        </div>

        {/* Draggable divider (desktop only). */}
        <div
          role="separator"
          aria-orientation="vertical"
          onPointerDown={onResizerPointerDown}
          onPointerMove={dragging ? onResizerPointerMove : undefined}
          onPointerUp={onResizerPointerUp}
          onDoubleClick={() => setRatio(DEFAULT_SPLIT_RATIO)}
          title="Húzd az átméretezéshez — dupla kattintás: alaphelyzet"
          className="group hidden lg:flex shrink-0 items-center justify-center cursor-col-resize bg-brand-200 hover:bg-brand-300 transition-colors"
          style={{ width: RESIZER_PX }}
        >
          <div className="h-8 w-0.5 rounded-full bg-brand-400 group-hover:bg-brand-500" />
        </div>

        <div
          className={`hidden lg:block flex-1 min-w-0 min-h-0 ${
            dragging ? "pointer-events-none" : ""
          }`}
        >
          {preview}
        </div>

        {/* While dragging, a full-screen overlay keeps the resize cursor and
            stops the preview iframe from swallowing pointer events. */}
        {dragging && (
          <div className="fixed inset-0 z-50 cursor-col-resize select-none" />
        )}
      </div>

      {/* Mobile / tablet preview overlay */}
      {mobilePreviewOpen && (
        <div className="fixed inset-0 z-50 flex flex-col bg-white lg:hidden">
          <div className="flex items-center justify-between px-4 h-12 border-b border-brand-200 shrink-0">
            <span className="text-sm font-semibold">Élő előnézet</span>
            <button
              type="button"
              onClick={() => setMobilePreviewOpen(false)}
              className="p-2 rounded text-brand-500 hover:bg-brand-100"
              aria-label="Bezárás"
            >
              <X size={18} />
            </button>
          </div>
          <div className="flex-1 min-h-0">{preview}</div>
        </div>
      )}
    </div>
  );
}
